import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Check, 
  Clock, 
  AlertTriangle, 
  Package, 
  Filter,
  Settings,
  Search
} from 'lucide-react';
import { useNotifications } from '../hooks/useNotifications';
import { GRADIENT_UTILS } from '../lib/styleGuide';

const COLORS = {
  PRIMARY_BROWN: '#8B4513',
  NEUTRAL_WHITE: '#FFFFFF',
  ERROR_RED: '#EF4444',
  SUCCESS_GREEN: '#10B981',
  WARNING_ORANGE: '#F59E0B',
  ACCENT_GRAY: '#6B7280',
  BORDER_GRAY: '#E5E7EB',
  NEUTRAL_BG: '#F9FAFB',
  NEUTRAL_TEXT: '#1F2937'
};

const FONTS = {
  body: { fontFamily: 'Inter, system-ui, sans-serif' },
  heading: { fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600 }
};

const NOTIFICATION_TYPES = {
  NEW_ORDER: {
    label: 'New Orders',
    icon: Package,
    color: COLORS.SUCCESS_GREEN,
    bgColor: '#ECFDF5'
  },
  DUE_SOON: {
    label: 'Due Soon',
    icon: Clock,
    color: COLORS.WARNING_ORANGE,
    bgColor: '#FFFBEB'
  },
  OVERDUE: {
    label: 'Overdue',
    icon: AlertTriangle,
    color: COLORS.ERROR_RED,
    bgColor: '#FEF2F2'
  },
  LOW_INVENTORY: {
    label: 'Low Inventory',
    icon: Package,
    color: COLORS.ERROR_RED,
    bgColor: '#FEF2F2'
  }
};

export default function SellerNotifications() {
  const navigate = useNavigate();
  const { 
    notifications, 
    unreadCount, 
    loading, 
    error, 
    filters, 
    stats,
    markAsRead, 
    markAllAsRead, 
    updateFilters 
  } = useNotifications();

  const [showFilters, setShowFilters] = useState(false);

  const handleNotificationClick = async (notification) => {
    // Mark as read when clicked
    if (notification.status === 'UNREAD') {
      await markAsRead(notification.id);
    }
    
    // Navigate to the primary action
    if (notification.primary_action?.href) {
      navigate(notification.primary_action.href);
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const notificationDate = timestamp?.toDate?.() || new Date(timestamp);
    const diffInMinutes = Math.floor((now - notificationDate) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return notificationDate.toLocaleDateString();
  };

  const getTypeConfig = (type) => {
    return NOTIFICATION_TYPES[type] || {
      label: type,
      icon: Package,
      color: COLORS.ACCENT_GRAY,
      bgColor: COLORS.NEUTRAL_BG
    };
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: COLORS.NEUTRAL_BG,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...FONTS.body
      }}>
        <div style={{ color: COLORS.ACCENT_GRAY }}>Loading notifications...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: COLORS.NEUTRAL_BG,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...FONTS.body
      }}>
        <div style={{ color: COLORS.ERROR_RED }}>Error: {error}</div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: COLORS.NEUTRAL_BG,
      ...FONTS.body
    }}>
      {/* Header */}
      <div style={{
        background: COLORS.NEUTRAL_WHITE,
        borderBottom: `1px solid ${COLORS.BORDER_GRAY}`,
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => navigate('/seller-hub')}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = COLORS.NEUTRAL_BG}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <ArrowLeft size={20} color={COLORS.PRIMARY_BROWN} />
          </button>
          
          <div>
            <h1 style={{ 
              fontSize: '20px', 
              fontWeight: 600, 
              color: COLORS.NEUTRAL_TEXT, 
              margin: 0,
              ...FONTS.heading
            }}>
              Notifications
            </h1>
            {unreadCount > 0 && (
              <p style={{ 
                fontSize: '14px', 
                color: COLORS.ACCENT_GRAY, 
                margin: '4px 0 0 0',
                ...FONTS.body
              }}>
                {unreadCount} unread
              </p>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = COLORS.NEUTRAL_BG}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <Filter size={20} color={COLORS.PRIMARY_BROWN} />
          </button>
          
          <button
            onClick={() => navigate('/seller-hub/notifications/settings')}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = COLORS.NEUTRAL_BG}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <Settings size={20} color={COLORS.PRIMARY_BROWN} />
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div style={{
          background: COLORS.NEUTRAL_WHITE,
          borderBottom: `1px solid ${COLORS.BORDER_GRAY}`,
          padding: '16px 20px'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Type Filter */}
            <div>
              <label style={{ 
                fontSize: '14px', 
                fontWeight: 500, 
                color: COLORS.NEUTRAL_TEXT,
                marginBottom: '8px',
                display: 'block',
                ...FONTS.body
              }}>
                Type
              </label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {Object.entries(NOTIFICATION_TYPES).map(([type, config]) => (
                  <button
                    key={type}
                    onClick={() => updateFilters({ type: filters.type === type ? 'ALL' : type })}
                    style={{
                      background: filters.type === type ? COLORS.PRIMARY_BROWN : COLORS.NEUTRAL_BG,
                      color: filters.type === type ? COLORS.NEUTRAL_WHITE : COLORS.NEUTRAL_TEXT,
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: '16px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      ...FONTS.body
                    }}
                  >
                    {config.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label style={{ 
                fontSize: '14px', 
                fontWeight: 500, 
                color: COLORS.NEUTRAL_TEXT,
                marginBottom: '8px',
                display: 'block',
                ...FONTS.body
              }}>
                Status
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                {['ALL', 'UNREAD', 'READ'].map((status) => (
                  <button
                    key={status}
                    onClick={() => updateFilters({ status })}
                    style={{
                      background: filters.status === status ? COLORS.PRIMARY_BROWN : COLORS.NEUTRAL_BG,
                      color: filters.status === status ? COLORS.NEUTRAL_WHITE : COLORS.NEUTRAL_TEXT,
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: '16px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      ...FONTS.body
                    }}
                  >
                    {status === 'ALL' ? 'All' : status === 'UNREAD' ? 'Unread' : 'Read'}
                  </button>
                ))}
              </div>
            </div>

            {/* Time Filter */}
            <div>
              <label style={{ 
                fontSize: '14px', 
                fontWeight: 500, 
                color: COLORS.NEUTRAL_TEXT,
                marginBottom: '8px',
                display: 'block',
                ...FONTS.body
              }}>
                Time
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                {[
                  { label: 'All', value: null },
                  { label: 'Today', value: new Date().toISOString().split('T')[0] },
                  { label: 'Last 7 days', value: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString() },
                  { label: 'Last 30 days', value: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString() }
                ].map(({ label, value }) => (
                  <button
                    key={label}
                    onClick={() => updateFilters({ since: value })}
                    style={{
                      background: filters.since === value ? COLORS.PRIMARY_BROWN : COLORS.NEUTRAL_BG,
                      color: filters.since === value ? COLORS.NEUTRAL_WHITE : COLORS.NEUTRAL_TEXT,
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: '16px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      ...FONTS.body
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      {unreadCount > 0 && (
        <div style={{
          background: COLORS.NEUTRAL_WHITE,
          borderBottom: `1px solid ${COLORS.BORDER_GRAY}`,
          padding: '12px 20px',
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={markAllAsRead}
            style={{
              ...GRADIENT_UTILS.getGradientButton(),
              padding: '8px 16px',
              borderRadius: '8px',
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s ease',
              ...FONTS.body
            }}
          >
            <Check size={16} />
            Mark all as read
          </button>
        </div>
      )}

      {/* Notifications List */}
      <div style={{ padding: '20px' }}>
        {notifications.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            color: COLORS.ACCENT_GRAY
          }}>
            <Package size={48} color={COLORS.ACCENT_GRAY} style={{ marginBottom: '16px' }} />
            <h3 style={{ 
              fontSize: '18px', 
              fontWeight: 600, 
              margin: '0 0 8px 0',
              ...FONTS.heading
            }}>
              No notifications
            </h3>
            <p style={{ 
              fontSize: '14px', 
              margin: 0,
              ...FONTS.body
            }}>
              You're all caught up! Check back later for new orders and updates.
            </p>
            <button
              onClick={() => navigate('/orders')}
              style={{
                ...GRADIENT_UTILS.getGradientButton(),
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '14px',
                cursor: 'pointer',
                marginTop: '16px',
                transition: 'all 0.2s ease',
                ...FONTS.body
              }}
            >
              View Orders
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {notifications.map((notification) => {
              const typeConfig = getTypeConfig(notification.type);
              const IconComponent = typeConfig.icon;
              
              return (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  style={{
                    background: COLORS.NEUTRAL_WHITE,
                    border: `1px solid ${COLORS.BORDER_GRAY}`,
                    borderRadius: '12px',
                    padding: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    borderLeft: `4px solid ${typeConfig.color}`,
                    ...FONTS.body
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    {/* Icon */}
                    <div style={{
                      background: typeConfig.bgColor,
                      color: typeConfig.color,
                      padding: '8px',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <IconComponent size={16} />
                    </div>

                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <h3 style={{ 
                          fontSize: '16px', 
                          fontWeight: 600, 
                          color: COLORS.NEUTRAL_TEXT,
                          margin: 0,
                          ...FONTS.heading
                        }}>
                          {notification.title}
                        </h3>
                        
                        {notification.status === 'UNREAD' && (
                          <div style={{
                            width: '8px',
                            height: '8px',
                            background: COLORS.PRIMARY_BROWN,
                            borderRadius: '50%',
                            flexShrink: 0
                          }} />
                        )}
                      </div>
                      
                      <p style={{ 
                        fontSize: '14px', 
                        color: COLORS.ACCENT_GRAY,
                        margin: '0 0 8px 0',
                        lineHeight: '1.4',
                        ...FONTS.body
                      }}>
                        {notification.message || notification.body || 'No message available'}
                      </p>
                      
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ 
                          fontSize: '12px', 
                          color: COLORS.ACCENT_GRAY,
                          ...FONTS.body
                        }}>
                          {formatTimeAgo(notification.created_at)}
                        </span>
                        
                        {notification.primary_action && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNotificationClick(notification);
                            }}
                            style={{
                              ...GRADIENT_UTILS.getGradientButton(),
                              padding: '6px 12px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              ...FONTS.body
                            }}
                          >
                            {notification.primary_action.label}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
